import { useState, useRef } from 'react'
import * as XLSX from 'xlsx'
import { supabase, addMember } from '../lib/supabase'

function detectColumns(headers) {
  const lower = headers.map(h => h?.toString().toLowerCase().trim())
  return {
    nameCol: Math.max(0, lower.findIndex(h => h.includes('name') || h.includes('student') || h.includes('employee') || h.includes('member'))),
    idCol: Math.max(1, lower.findIndex(h => h.includes('index') || h.includes('id') || h.includes('number') || h.includes('no') || h.includes('staff') || h.includes('badge') || h.includes('reg'))),
    groupCol: lower.findIndex(h => h.includes('course') || h.includes('class') || h.includes('group') || h.includes('dept') || h.includes('department') || h.includes('programme')),
  }
}

export default function MemberUpload({ org, L, onDone }) {
  const [step, setStep] = useState('idle')
  const [rows, setRows] = useState([])
  const [colMap, setColMap] = useState({ nameCol: 0, idCol: 1, groupCol: -1 })
  const [headers, setHeaders] = useState([])
  const [progress, setProgress] = useState(0)
  const [errors, setErrors] = useState([])
  const [added, setAdded] = useState(0)
  const [fileName, setFileName] = useState('')
  const fileRef = useRef()

  function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = evt => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
        if (data.length < 2) { setStep('error'); return }
        const hdrs = data[0].map(h => h?.toString())
        setHeaders(hdrs)
        setColMap(detectColumns(hdrs))
        setRows(data.slice(1).filter(r => r.some(c => c !== '')))
        setStep('preview')
      } catch { setStep('error') }
    }
    reader.readAsBinaryString(file)
  }

  async function handleUpload() {
    setStep('uploading'); setProgress(0)
    const errs = []
    const skipped = []
    let added = 0

    // ── Step 1: Deduplicate within the file itself ─────────────────
    const seen = new Set()
    const dedupedRows = []
    for (const row of rows) {
      const member_id = row[colMap.idCol]?.toString().trim()
      if (!member_id) { dedupedRows.push(row); continue }
      if (seen.has(member_id)) {
        skipped.push(`Skipped duplicate in file: ID ${member_id}`)
      } else {
        seen.add(member_id)
        dedupedRows.push(row)
      }
    }

    // ── Step 2: Fetch existing member IDs in this org ──────────────
    const { data: existingMembers } = await supabase
      .from('members')
      .select('member_id')
      .eq('org_id', org.id)
    const existingIds = new Set((existingMembers || []).map(m => m.member_id?.toString().trim()))

    // ── Step 3: Insert only new, valid rows ────────────────────────
    for (let i = 0; i < dedupedRows.length; i++) {
      const row = dedupedRows[i]
      const name = row[colMap.nameCol]?.toString().trim()
      const member_id = row[colMap.idCol]?.toString().trim()
      const group_name = colMap.groupCol >= 0 ? row[colMap.groupCol]?.toString().trim() : ''

      if (!name || !member_id) {
        errs.push(`Row ${i + 2}: missing name or ID — skipped`)
        setProgress(Math.round(((i + 1) / dedupedRows.length) * 100))
        continue
      }

      if (existingIds.has(member_id)) {
        skipped.push(`${name} (${member_id}): already exists — skipped`)
        setProgress(Math.round(((i + 1) / dedupedRows.length) * 100))
        continue
      }

      const { error } = await addMember({ name, member_id, group_name, org_id: org.id })
      if (error) {
        errs.push(`${name} (${member_id}): ${error.message}`)
      } else {
        added++
        existingIds.add(member_id) // prevent re-insert if same id appears again
      }
      setProgress(Math.round(((i + 1) / dedupedRows.length) * 100))
    }

    setErrors([...skipped, ...errs])
    setAdded(added)
    setStep('done')
    // Always refresh the member list — even partial uploads should show
    onDone?.()
  }

  function reset() { setStep('idle'); setRows([]); setHeaders([]); setFileName(''); setErrors([]) }

  return (
    <div>
      {step === 'idle' && (
        <>
          <div
            onClick={() => fileRef.current.click()}
            style={{ border: '2px dashed var(--border)', borderRadius: 'var(--radius-lg)', padding: '2rem', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.background = 'var(--blue-light)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'transparent' }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Upload {L.members} list</div>
            <div className="text-sm text-muted mb-2">Excel (.xlsx, .xls) or CSV file</div>
            <div className="text-xs text-muted">Columns needed: Name, {L.idField}, {L.group} (optional)</div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFile} />
          </div>
          <div className="alert alert-info mt-3" style={{ fontSize: 12 }}>
            <strong>Tip:</strong> Your file should have a header row. Column order doesn't matter — you can remap them before uploading.
          </div>
        </>
      )}

      {step === 'preview' && (
        <div>
          <div className="alert alert-info mb-3">
            Found <strong>{rows.length} {L.members?.toLowerCase()}</strong> in <strong>{fileName}</strong>. Check column mapping below.
          </div>
          <div className="card mb-3">
            <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 10 }}>Column mapping</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10 }}>
              {[
                { label: 'Name column', key: 'nameCol', required: true },
                { label: `${L.idField} column`, key: 'idCol', required: true },
                { label: `${L.group} column (optional)`, key: 'groupCol', required: false },
              ].map(f => (
                <div key={f.key} className="form-group" style={{ margin: 0 }}>
                  <label className="field-label">{f.label}</label>
                  <select value={colMap[f.key]} onChange={e => setColMap({ ...colMap, [f.key]: parseInt(e.target.value) })}>
                    {!f.required && <option value={-1}>— Not in file —</option>}
                    {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div className="table-wrap mb-3" style={{ maxHeight: 220, overflowY: 'auto' }}>
            <table>
              <thead><tr><th>Name</th><th>{L.idField}</th>{colMap.groupCol >= 0 && <th>{L.group}</th>}</tr></thead>
              <tbody>
                {rows.slice(0, 8).map((row, i) => (
                  <tr key={i}>
                    <td>{row[colMap.nameCol]}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{row[colMap.idCol]}</td>
                    {colMap.groupCol >= 0 && <td>{row[colMap.groupCol]}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 8 && <p className="text-xs text-muted text-center" style={{ padding: 8 }}>...and {rows.length - 8} more</p>}
          </div>
          <div className="flex gap-2">
            <button className="btn btn-secondary" onClick={reset}>Cancel</button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleUpload}>Upload {rows.length} {L.members?.toLowerCase()} →</button>
          </div>
        </div>
      )}

      {step === 'uploading' && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ marginBottom: 12, fontWeight: 500 }}>Uploading {L.members?.toLowerCase()}...</div>
          <div className="progress-track"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
          <div className="text-sm text-muted mt-2">{progress}% — please don't close this page</div>
        </div>
      )}

      {step === 'done' && (
        <div>
          <div className={`alert ${errors.length === 0 ? 'alert-success' : 'alert-warning'} mb-3`}>
            {added > 0 ? `✓ ${added} ${L.members?.toLowerCase()} added successfully.` : 'No new members added.'}{errors.length > 0 ? ` ${errors.length} skipped or failed.` : ''}
          </div>
          {errors.length > 0 && (
            <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: '0.75rem', fontSize: 12, color: 'var(--text-muted)', marginBottom: '1rem', maxHeight: 100, overflowY: 'auto' }}>
              {errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          <div className="flex gap-2">
            <button className="btn btn-secondary" onClick={reset}>Upload another</button>
            <button className="btn btn-primary" onClick={() => { reset(); onDone?.() }}>Done</button>
          </div>
        </div>
      )}

      {step === 'error' && (
        <div>
          <div className="alert alert-error mb-3">Could not read this file. Make sure it's a valid Excel or CSV file with at least one header row and one data row.</div>
          <button className="btn btn-secondary" onClick={reset}>Try again</button>
        </div>
      )}
    </div>
  )
}
